import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Block, Button, Row, Col } from "../../../components/Component";
import { Label, Input } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import swal from "sweetalert";
import moment from "moment";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";

export default function PartnerSupportRequestDetail() {
  const { id } = useParams();
  const history = useHistory();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    http
      .get(`/api/v1/admin/support-requests/${id}`)
      .then((res) => {
        const d = res.data?.data;
        setRow(d || null);
        setNotes(d?.internalNotes != null ? String(d.internalNotes) : "");
      })
      .catch(() => {
        setRow(null);
        swal("Error", "Could not load this request.", "error");
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const partner = row?.partnerId && typeof row.partnerId === "object" ? row.partnerId : null;
  const user = row?.userId && typeof row.userId === "object" ? row.userId : null;
  const clinic = row?.clinicId && typeof row.clinicId === "object" ? row.clinicId : null;

  const saveNotes = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await http.patch(`/api/v1/admin/support-requests/${id}`, { internalNotes: notes });
      swal("Saved", "Internal notes updated.", "success");
      load();
    } catch (e) {
      swal("Error", e.response?.data?.error || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const markResolved = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await http.patch(`/api/v1/admin/support-requests/${id}`, { status: "resolved", internalNotes: notes });
      swal("Updated", "Marked as resolved.", "success");
      load();
    } catch (e) {
      swal("Error", e.response?.data?.error || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !row) {
    return (
      <TeleconsultationPageShell headTitle="Support request" title="Support request" description={false}>
        <Block className="text-center py-5">Loading…</Block>
      </TeleconsultationPageShell>
    );
  }

  if (!row) {
    return (
      <TeleconsultationPageShell headTitle="Support request" title="Support request" description={false}>
        <Block>Not found.</Block>
      </TeleconsultationPageShell>
    );
  }

  return (
    <TeleconsultationPageShell
      headTitle="Support request detail"
      title="Support request"
      description={
        <Button color="light" outline size="sm" type="button" onClick={() => history.push(getPublicUrl("teleconsultations/support-requests"))}>
          ← Back to list
        </Button>
      }
    >
      <Block>
        <Row className="g-3 mb-3">
          <Col md="6">
            <p className="text-soft small mb-1">Created</p>
            <p className="mb-0">{moment(row.createdAt).format("YYYY-MM-DD HH:mm")}</p>
          </Col>
          <Col md="6">
            <p className="text-soft small mb-1">Status</p>
            <p className="mb-0 fw-semibold">{row.status}</p>
          </Col>
          <Col md="6">
            <p className="text-soft small mb-1">Partner</p>
            <p className="mb-0">{partner?.organization_name || "—"}</p>
          </Col>
          <Col md="6">
            <p className="text-soft small mb-1">Submitted by</p>
            <p className="mb-0">
              {user ? `${[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "—"} (${user.email || "—"})` : "—"}
            </p>
          </Col>
          <Col md="6">
            <p className="text-soft small mb-1">Type</p>
            <p className="mb-0">{row.type}</p>
          </Col>
          <Col md="6">
            <p className="text-soft small mb-1">Calendar type</p>
            <p className="mb-0">{row.calendarType || "—"}</p>
          </Col>
          <Col md="12">
            <p className="text-soft small mb-1">Clinic</p>
            <p className="mb-0">{clinic?.name || (row.clinicId ? String(row.clinicId) : "—")}</p>
          </Col>
          <Col md="12">
            <p className="text-soft small mb-1">Message</p>
            <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {row.message || "—"}
            </p>
          </Col>
        </Row>

        <hr />

        <Row className="g-3">
          <Col md="12">
            <Label htmlFor="sr-internal-notes">Internal notes</Label>
            <Input
              id="sr-internal-notes"
              type="textarea"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
            />
          </Col>
          <Col md="12" className="d-flex flex-wrap gap-2">
            <Button color="primary" type="button" disabled={saving} onClick={saveNotes}>
              Save notes
            </Button>
            {row.status !== "resolved" && (
              <Button color="success" type="button" disabled={saving} onClick={markResolved}>
                Mark as resolved
              </Button>
            )}
          </Col>
        </Row>
      </Block>
    </TeleconsultationPageShell>
  );
}
